import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  MenuItem,
} from "@mui/material";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import Q8Breadcrumbs from "../components/Q8Breadcrumbs";
import Q8Button from "../components/Q8Button";
import Q8SnackBar from "../components/Q8SnackBar";
import {
  Add as AddIcon,
  FileDownload as ExportIcon,
  FilterList as FilterIcon,
  Delete as DeleteIcon,
  ViewColumn as ColumnIcon,
  InfoOutlined,
} from "@mui/icons-material";
import "tabulator-tables/dist/css/tabulator.min.css";
import "../styles/tabulator.css";
import * as XLSX from "xlsx";
import LevelOpexService from "../service/levelOpexService";

const Level2Table = () => {
  const tableRef = useRef(null);
  const [table, setTable] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "default",
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, ids: [] });
  const [addDialog, setAddDialog] = useState({ open: false });
  const [newRecord, setNewRecord] = useState({
    level2_code: "",
    level2_desc: "",
    level1_code: "",
  });
  const [level1Options, setLevel1Options] = useState([]);
  const [filtersActive, setFiltersActive] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    level1_desc: true,
    level1_info: true,
  });

  const showNotification = (message, type = "default") => {
    setSnackbar({ open: true, message, type });
  };

  const handleAddDialogOpen = () => {
    setAddDialog({ open: true });
    fetchLevel1Options();
  };

  const handleAddDialogClose = () => {
    setAddDialog({ open: false });
    setNewRecord({
      level2_code: "",
      level2_desc: "",
      level1_code: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRecord((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const fetchData = async () => {
    try {
      const data = await LevelOpexService.getLevels2();
      if (table) {
        table.setData(data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showNotification("Error loading data", "error");
    }
  };

  const fetchLevel1Options = async () => {
    try {
      const data = await LevelOpexService.getLevels1();
      setLevel1Options(data);
    } catch (error) {
      console.error("Error fetching Level 1 data:", error);
      showNotification("Error loading Level 1 options", "error");
    }
  };

  const handleDownloadExcel = () => {
    if (table) {
      const data = table.getData();
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, "Level2 Data");
      XLSX.writeFile(wb, "Level2_data.xlsx");
    }
  };

  const handleAddNew = async () => {
    if (
      !newRecord.level2_code ||
      !newRecord.level2_desc ||
      !newRecord.level1_code
    ) {
      showNotification("Compilare tutti i campi obbligatori.", "error");
      return;
    }
    try {
      await LevelOpexService.insertLevel2(newRecord);
      await fetchData();
      showNotification("New record added successfully", "success");
      handleAddDialogClose();
    } catch (error) {
      console.error("Error adding new record:", error);
      showNotification("Error adding new record", "error");
    }
  };

  const handleDeleteSelected = () => {
    const selectedRows = table.getSelectedRows();
    if (selectedRows.length === 0) {
      showNotification("Selezionare quali record eliminare.", "warning");
      return;
    }
    const selectedIds = selectedRows.map((row) => row.getData().id);
    setDeleteDialog({ open: true, ids: selectedIds });
  };

  const handleDelete = async (ids) => {
    try {
      for (const id of ids) {
        await LevelOpexService.deleteLevel2(id);
      }
      await fetchData();
      showNotification(
        `Successfully deleted ${ids.length} record(s)`,
        "success"
      );
    } catch (error) {
      console.error("Error deleting records:", error);
      showNotification("Error deleting records", "error");
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, ids: [] });
  };

  const handleDeleteConfirm = async () => {
    if (deleteDialog.ids.length > 0) {
      await handleDelete(deleteDialog.ids);
      setDeleteDialog({ open: false, ids: [] });
    }
  };

  useEffect(() => {
    if (table) {
      const column = table.getColumn("level1_code");
      LevelOpexService.getLevels1().then((data) => {
        const values = data.map((item) => ({
          value: item.level1_code,
          label: `${item.level1_code} - ${item.level1_desc}`,
        }));
        column.updateDefinition({
          editorParams: {
            values,
            displayField: "label",
            valueField: "value",
          },
          headerFilterParams: {
            values,
            displayField: "label",
            valueField: "value",
          },
        });
      });
    }
  }, [table]);

  useEffect(() => {
    if (table) {
      const tableElement = table.element;
      tableElement.classList.add("hide-filters");
    }
  }, [table]);

  useEffect(() => {
    let tableInstance = null;

    if (tableRef.current && !table) {
      const resizeTable = () => {
        if (tableRef.current) {
          const height = tableRef.current.parentElement.clientHeight;
          tableRef.current.style.height = `${height}px`;
          if (table) {
            table.redraw(true);
          }
        }
      };

      const resizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => {
          resizeTable();
        });
      });

      if (tableRef.current) {
        resizeObserver.observe(tableRef.current.parentElement);
      }

      const columns = [
        {
          formatter: "rowSelection",
          titleFormatter: "rowSelection",
          titleFormatterParams: { rowRange: "active" },
          hozAlign: "center",
          headerSort: false,
          width: 30,
          cellClick: function (_, cell) {
            const row = cell.getRow();

            if (row.isSelected()) {
              row.deselect();
            } else {
              row.select();
            }
          },
        },
        {
          title: "Level2 Code",
          field: "level2_code",
          widthGrow: 1,
          editor: "input",
          validator: ["required"],
          editable: true,
          headerFilter: "input",
          cssClass: "editable-column",
        },
        {
          title: "Description",
          field: "level2_desc",
          widthGrow: 2,
          editor: "input",
          validator: "required",
          editable: true,
          headerFilter: "input",
          cssClass: "editable-column",
        },
        {
          title: "Level1 Code",
          field: "level1_code",
          widthGrow: 1,
          editor: "list",
          validator: "required",
          editable: true,
          editorParams: {},
          headerFilter: "list",
          headerFilterParams: {},
          cssClass: "editable-column",
        },
        {
          title: "Level1 Description",
          field: "level1_desc",
          widthGrow: 2,
          headerFilter: "input",
          editable: false,
          frozen: true,
        },
        // {
        //   title: "Level1 Info",
        //   field: "level1_info",
        //   widthGrow: 2,
        //   headerFilter: "input",
        //   editable: false,
        //   frozen: true,
        // },
      ];

      // Important changes to table initialization options
      const newTable = new Tabulator(tableRef.current, {
        columns: columns,
        layout: "fitDataFill",
        pagination: "local",
        paginationSize: 100,
        paginationSizeSelector: [10, 25, 50, 100],
        movableColumns: true,
        height: "100%",
        placeholder: "Nessun dato disponibile",
        initialSort: [{ column: "level2_code", dir: "asc" }],
        rowHeight: 40,
        selectable: true,
        selectableRangeMode: "click",
        selectablePersistence: true,
        responsiveLayout: false,
        autoResize: false,
        selectableRange: true,
        selectableRangeColumns: false,
        selectableRangeRows: true,
        selectableRangeClearCells: true,
        navigationMode: "cell",
        clipboard: true,
        clipboardCopyRowRange: "range",
        clipboardPasteParser: "range",
        clipboardPasteAction: "range",
        editTriggerEvent: "dblclick",
        headerFilterLiveFilter: true,
        headerFilterPlaceholder: "Filter...",
        clipboardCopyConfig: {
          rowHeaders: false,
          columnHeaders: false,
        },
        clipboardCopyStyled: false,
        // Disable the problematic ResizeColumns module
        resizableColumns: false,
      });

      newTable.on("cellEdited", async (cell) => {
        try {
          const row = cell.getRow();
          const rowData = row.getData();

          if (cell.getField() === "level1_code") {
            const level1Data = await LevelOpexService.getLevels1();
            const values = level1Data.map((item) => ({
              value: item.level1_code,
              label: `${item.level1_code} - ${item.level1_desc}`,
            }));

            cell
              .getTable()
              .getColumn("level1_code")
              .updateDefinition({
                editorParams: {
                  values,
                  displayField: "label",
                  valueField: "value",
                },
                headerFilterParams: {
                  values,
                  displayField: "label",
                  valueField: "value",
                },
              });

            // Aggiorna anche la descrizione del level1
            const selectedLevel1 = level1Data.find(
              (item) => item.level1_code === cell.getValue()
            );
            if (selectedLevel1) {
              row.update({
                ...rowData,
                level1_desc: selectedLevel1.level1_desc,
                level1_info: `${selectedLevel1.level1_code} - ${selectedLevel1.level1_desc}`,
              });
            }
          }

          const updatedData = await LevelOpexService.updateLevel2(rowData);
          cell.getTable().updateData([updatedData]);
          showNotification("Changes saved successfully", "success");
        } catch (error) {
          console.error("Error saving data:", error);
          cell.restoreOldValue();
          showNotification("Error saving changes", "error");
        }
      });

      setTable(newTable);

      return () => {
        if (table) {
          table.destroy();
        }
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    }
  }, []);

  useEffect(() => {
    if (table) {
      fetchData();
    }
  }, [table]);

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <Q8Breadcrumbs
        links={[{ label: "Level 2", href: "/level2" }]}
        variant="primary"
      />

      <Box sx={{ mt: 3, mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="h4" sx={{ color: "primary.main" }}>
          Level2 OPEX
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Q8Button
            variant="primary"
            label="Add New"
            icon={<AddIcon />}
            onClick={handleAddDialogOpen}
            size="small"
          />
          <Q8Button
            variant="secondary"
            label="Export Excel"
            icon={<ExportIcon />}
            onClick={handleDownloadExcel}
            size="small"
          />
          <Q8Button
            variant="tertiary"
            label={filtersActive ? "Hide Filters" : "Show Filters"}
            icon={<FilterIcon />}
            onClick={() => {
              if (table) {
                const newState = !filtersActive;
                setFiltersActive(newState);

                const tableElement = table.element;
                if (newState) {
                  tableElement.classList.remove("hide-filters");
                } else {
                  tableElement.classList.add("hide-filters");
                  table.clearHeaderFilter();
                }
              }
            }}
            size="small"
          />
          <Q8Button
            variant="error"
            label="Delete Selected"
            icon={<DeleteIcon />}
            onClick={handleDeleteSelected}
            size="small"
          />
        </Box>
      </Box>

      <Box
        sx={{
          my: 1,
          p: 2,
          bgcolor: "#FFF8E1",
          border: "1px solid #FFE082",
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <InfoOutlined sx={{ color: "#FB8C00" }} />
        <Typography variant="body2">
          Per modificare un record, scegliere una colonna modificabile (colonne
          in giallo) ed effettuare un doppio click sulla cella per effettuare
          una modifica.
        </Typography>
      </Box>

      <Card sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <div ref={tableRef} style={{ flex: 1 }}></div>
      </Card>

      <Dialog
        open={addDialog.open}
        onClose={handleAddDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Aggiungi nuovo Record Livello 2</DialogTitle>
        <DialogContent>
          <Box
            component="form"
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 2,
              py: 2,
            }}
          >
            <TextField
              label="Level2 Code"
              name="level2_code"
              value={newRecord.level2_code}
              onChange={handleInputChange}
              fullWidth
              required
              error={!newRecord.level2_code}
              helperText={!newRecord.level2_code ? "Campo obbligatorio" : ""}
            />
            <TextField
              label="Description"
              name="level2_desc"
              value={newRecord.level2_desc}
              onChange={handleInputChange}
              fullWidth
              required
              error={!newRecord.level2_desc}
              helperText={!newRecord.level2_desc ? "Campo obbligatorio" : ""}
            />
            <TextField
              select
              label="Level1 Code"
              name="level1_code"
              value={newRecord.level1_code}
              onChange={handleInputChange}
              fullWidth
              required
              error={!newRecord.level1_code}
              helperText={!newRecord.level1_code ? "Campo obbligatorio" : ""}
            >
              {level1Options.map((option) => (
                <MenuItem
                  key={option.id}
                  value={option.level1_code}
                  onClick={() => {
                    setNewRecord((prev) => ({
                      ...prev,
                      level1_code: option.level1_code,
                      level1_desc: option.level1_desc,
                      level1_info: `${option.level1_code} - ${option.level1_desc}`,
                    }));
                  }}
                >
                  {option.level1_code} - {option.level1_desc}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Q8Button
            variant="secondary"
            label="Annulla"
            onClick={handleAddDialogClose}
          />
          <Q8Button variant="primary" label="Aggiungi" onClick={handleAddNew} />
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Sei sicuro di voler eliminare {deleteDialog.ids.length}
            record(s) selezionati? Quest'azione non può essere annullata.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Q8Button
            variant="secondary"
            label="Annulla"
            onClick={handleDeleteCancel}
          />
          <Q8Button
            variant="primary"
            label="Delete"
            onClick={handleDeleteConfirm}
            sx={{ backgroundColor: "#cc0e00" }}
          />
        </DialogActions>
      </Dialog>

      <Q8SnackBar
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default Level2Table;
